import LocalizedLink from '@components/Localization/LocalizedLink'
import { Text } from '@components/gassan-ui/Typography/Text'
import { Box, CleanButton, Flex, Icon, Image, Link, OldHeading, OldText } from '@gassan-ui'
import { CartItem } from '@generated'
import { dataLayerRemoveFromCart } from '@lib/data-layer-social-ads/remove-from-cart'
import { imgix } from '@lib/imigx'
import { articleIdsWithoutSizeDropdown } from '@lib/kontent/article-ids-without-size-dropdown'
import { GIFTCARD_ID } from '@pages/giftcard'
import { motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import Price from './Price'
import SelectQuantity from './SelectQuantity'
import SelectSize from './SelectSize'
import SizeInfo from './SizeInfo'
import * as S from './elements'
import { Description, Parent } from './elements'

type Props = {
  variant: 'loading' | 'updating' | 'idle'
  onRemove: () => void
  onChangeQuantity: (quantity: number) => void
  onChangeSize: (size: string) => void
  item: CartItem
  index: number
}

const CartItemRow: FC<Props> = ({
  variant,
  onRemove,
  onChangeQuantity,
  onChangeSize,
  item,
  index,
}) => {
  const { t } = useTranslation('other')
  const hideSizeDropdown = articleIdsWithoutSizeDropdown.includes(item.article.id)
  const isGiftcard = item.articleId === GIFTCARD_ID

  if (!item.article) {
    return null
  }

  const isRing = item.article.articleAttributes
    ? item.article.articleAttributes.findIndex(
        (attr) => attr.attributeID === 15 && attr.attributeValue === 'Ring',
      ) > -1
    : false
  return (
    <motion.div animate exit={{ opacity: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
      <Parent alignItems="flex-start">
        <Box bg="shades.100" position="relative" paddingTop="100%">
          {item.article.images[0] && (
            <LocalizedLink href={isGiftcard ? '/giftcard' : `/p/${item.article.id}`}>
              <a>
                <Image
                  className="absolute inset-0 bg-shade-100"
                  sources={[imgix(item.article.images[0], { width: 200 })]}
                  alt={item.article.brand}
                />
              </a>
            </LocalizedLink>
          )}
        </Box>
        <Box display={{ _: 'flex' }} flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center">
            <LocalizedLink href={isGiftcard ? '/giftcard' : `/p/${item.article.id}`}>
              <S.UnderlineA>
                <OldHeading mb={{ _: 0, small: '0.5rem', xlarge: '1rem' }} variant="h4">
                  {item.article.brand}
                </OldHeading>
              </S.UnderlineA>
            </LocalizedLink>

            <Box display={['contents', 'none']} color="tabaccoBrown">
              <CleanButton onClick={onRemove}>
                <Icon icon="trash-can" />
              </CleanButton>
            </Box>
          </Flex>
          <Box display={['contents', 'flex']} alignItems="center">
            <div>
              <Description mb={{ _: '0.25rem', small: '0.5rem', large: '1rem' }}>
                {item.article.description}
              </Description>
              {item.message && (
                <Text variant="small" className="mb-1 text-shade-500 sm:mb-2 lg:mb-4">
                  {`${t('additionalInfo')}: ${item.message}`}
                </Text>
              )}
            </div>

            <Box display={['contents', 'none']}>
              <Price
                price={item.article.price * item.quantity}
                salePrice={item.article.salePrice && item.article.salePrice * item.quantity}
                discount={
                  item.article.salePrice &&
                  Math.round((1 - item.article.salePrice / item.article.price) * 100)
                }
                status={variant === 'updating' ? 'loading' : 'idle'}
              />
            </Box>
          </Box>
          <Box>
            <Box
              display={{ _: 'grid', small: 'inline-grid', xlarge: 'none' }}
              gridTemplateColumns={{ _: '4.75rem 1fr', small: '4.75rem 9.5rem' }}
              mb={['0.5rem', '1rem']}
            >
              <SelectQuantity
                quantity={item.quantity}
                onChangeQuantity={(quantity) => onChangeQuantity(quantity)}
                disabled={isGiftcard}
              />
              {isRing && !hideSizeDropdown && (
                <SelectSize size={item.size} onChangeSize={(size) => onChangeSize(size)} />
              )}
            </Box>
            <Box
              display={{
                _: 'contents',
                small: item.size !== '' ? 'inline-block' : 'block',
                xlarge: 'none',
              }}
              ml={{ _: 0, small: item.size !== '' && '1rem', xlarge: 0 }}
              mb={[0, '1.5rem']}
            >
              {isRing && !hideSizeDropdown && <SizeInfo size={item.size} />}
            </Box>
            <Box display={['none', 'block']}>
              <Link as="button" onClick={handleRemoveItem} variant="brown">
                {t('remove')}
              </Link>
            </Box>
          </Box>
        </Box>
        <Box display={{ _: ' none', xlarge: 'block' }}>
          {isRing && !hideSizeDropdown && (
            <>
              <OldText mb="0.5rem" variant="small" as="span" display="block">
                {t('size')}
              </OldText>
              <SelectSize size={item.size} onChangeSize={(size) => onChangeSize(size)} />
              <Box mt="0.5rem">
                <SizeInfo size={item.size} />
              </Box>
            </>
          )}
        </Box>
        <Box display={{ _: ' none', xlarge: 'block' }}>
          <OldText mb="0.5rem" variant="small" as="span" display="block">
            {t('quantity')}
          </OldText>
          <SelectQuantity
            quantity={item.quantity}
            onChangeQuantity={(quantity) => onChangeQuantity(quantity)}
            disabled={isGiftcard}
          />
        </Box>
        <Box display={['none', 'contents']}>
          <Price
            price={item.article.price * item.quantity}
            salePrice={item.article.salePrice && item.article.salePrice * item.quantity}
            discount={
              item.article.salePrice &&
              Math.round((1 - item.article.salePrice / item.article.price) * 100)
            }
            status={variant === 'updating' ? 'loading' : 'idle'}
          />
        </Box>
      </Parent>
    </motion.div>
  )

  function handleRemoveItem() {
    dataLayerRemoveFromCart({
      item_id: item.article.id,
      item_name: item.article.description,
      currency: 'EUR',
      index,
      item_brand: item.article.brand,
      item_category: 'Apparel & Accessories',
      item_category2: 'Jewelry',
      item_category3: ['Horloge', 'Watch'].includes(item.article.articleType)
        ? 'Watches'
        : undefined,
      item_list_id: 'related_products',
      item_list_name: 'Related Products',
      price: Math.round((item.article.salePrice / 121 || item.article.price / 121) * 100) / 100,
      quantity: item.quantity,
    })
    onRemove()
  }
}

export default CartItemRow
